import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard, SaveBar } from '@/pages/settings/ClinicGeneral';
import type { Json } from '@/types/database.generated';

const WEEKDAYS = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lordag', 'sondag'] as const;
type Weekday = (typeof WEEKDAYS)[number];

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
}
type BusinessHoursShape = Record<Weekday, DayHours>;

const DEFAULT_BUSINESS_HOURS: BusinessHoursShape = {
  mandag: { enabled: true, start: '08:00', end: '16:00' },
  tirsdag: { enabled: true, start: '08:00', end: '16:00' },
  onsdag: { enabled: true, start: '08:00', end: '16:00' },
  torsdag: { enabled: true, start: '08:00', end: '16:00' },
  fredag: { enabled: true, start: '08:00', end: '16:00' },
  lordag: { enabled: false, start: '09:00', end: '14:00' },
  sondag: { enabled: false, start: '09:00', end: '14:00' },
};

function readBusinessHours(value: Json): BusinessHoursShape {
  const out = { ...DEFAULT_BUSINESS_HOURS };
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const day of WEEKDAYS) {
      const v = (value as Record<string, Json>)[day];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const obj = v as Record<string, Json>;
        out[day] = {
          enabled: Boolean(obj.enabled),
          start: typeof obj.start === 'string' ? obj.start : DEFAULT_BUSINESS_HOURS[day].start,
          end: typeof obj.end === 'string' ? obj.end : DEFAULT_BUSINESS_HOURS[day].end,
        };
      }
    }
  }
  return out;
}

export function ClinicHours() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [hours, setHours] = useState<BusinessHoursShape>(DEFAULT_BUSINESS_HOURS);

  useEffect(() => {
    if (!clinic) return;
    setHours(readBusinessHours(clinic.business_hours));
  }, [clinic]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({ business_hours: hours as unknown as Json });
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings (business_hours) failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <SectionCard
        title={t('settings.hours.title')}
        subtitle={t('settings.hours.subtitle')}
        readOnly={!canEdit}
      >
        <div className="space-y-4">
          {WEEKDAYS.map((day) => {
            const dh = hours[day];
            return (
              <div
                key={day}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="w-32">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={dh.enabled}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], enabled: e.target.checked },
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {t(`settings.hours.weekdays.${day}`)}
                    </span>
                  </label>
                </div>

                {dh.enabled ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="time"
                      value={dh.start}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], start: e.target.value },
                        }))
                      }
                      className="h-[44px] w-32 rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t('settings.hours.to')}
                    </span>
                    <input
                      type="time"
                      value={dh.end}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], end: e.target.value },
                        }))
                      }
                      className="h-[44px] w-32 rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                ) : (
                  <span className="text-sm italic text-gray-400">{t('settings.hours.closed')}</span>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}
